import axios, { AxiosResponse } from "axios";
import * as moment from "moment";

import Utils, { ResponseWrapper, ServerModelWithDateStrings } from "@teamprevent/common/utils";
import { CaseType } from "@teamprevent/common/model";

import { AddTaskModel, CreateTaskModel, TaskType } from "../task/model";

import { HealthSurveillanceCaseCreateModel } from "../healthSurveillance/models";

import { ImmunisationCaseCreateModel } from "../crud/ohCase/model";

import { mapCreateTaskModelToServer } from "../crud/ohCase/ohCaseApiMappers";

import * as models from "./models";

const apiUrl = "/api/triage";

const get = async (customerIds: string[], caseTypes: CaseType[], teamIds: string[]): Promise<models.TriageListModel[]> => {
    const response = await axios.get<ServerModelWithDateStrings<models.TriageListModel>[]>(`${apiUrl}`, { params: { customerIds, caseTypes, teamIds } });
    return response.data.map(parseListModel);
};

const getReferral = async (customerId: string, caseId: string): Promise<models.ReferralTriageDetailModel> => {
    const response = await axios.get<
        ServerModelWithDateStrings<ResponseWrapper<models.ReferralTriageDetailModel>>
    >(`${apiUrl}/${customerId}/${caseId}/referral${Utils.getOnlyParameters()}`);
    const result = Utils.CheckResponse(response.data);
    return parseReferralModel(result);
};

const getPpq = async (customerId: string, caseId: string): Promise<models.PpqTriageDetailModel> => {
    const response = await axios.get<
        ServerModelWithDateStrings<ResponseWrapper<models.PpqTriageDetailModel>>
    >(`${apiUrl}/${customerId}/${caseId}/ppq`);
    const result = Utils.CheckResponse(response.data);
    return parsePpqModel(result);
};

const getPhysiotherapyReferral = async (customerId: string, caseId: string): Promise<models.PhysiotherapyReferralTriageDetailModel> => {
    const response = await axios.get<
        ServerModelWithDateStrings<ResponseWrapper<models.PhysiotherapyReferralTriageDetailModel>>
    >(`${apiUrl}/${customerId}/${caseId}/physiotherapyreferral`);
    const result = Utils.CheckResponse(response.data);
    return parsePhysiotherapyReferralModel(result);
};

const getGenericCase = async (customerId: string, caseId: string): Promise<models.GenericCaseTriageDetailModel> => {
    const response = await axios.get<
        ServerModelWithDateStrings<ResponseWrapper<models.GenericCaseTriageDetailModel>>
    >(`${apiUrl}/${customerId}/${caseId}/genericCase${Utils.getOnlyParameters()}`);
    const result = Utils.CheckResponse(response.data);
    return parseGenericCaseModel(result);
};

const getReferralTriageTasks = async (): Promise<TaskType[]> => {
    const response = await axios.get(`${apiUrl}/ReferralTriageTasks`);
    return response.data;
};

const getPpqTriageTasks = async (): Promise<TaskType[]> => {
    const response = await axios.get(`${apiUrl}/PpqTriageTasks`);
    return response.data;
};

const getPhysiotherapyReferralTriageSuccessorTasks = async (): Promise<TaskType[]> => {
    const response = await axios.get(`${apiUrl}/PhysiotherapyReferralTriageSuccessorTasks`);
    return response.data;
};

const getGenericCaseTriageSuccessorTasks = async (): Promise<TaskType[]> => {
    const response = await axios.get(`${apiUrl}/GenericCaseTriageSuccessorTasks`);
    return response.data;
};

/**
 * @deprecated Replaced by TriageReferralDetail when FeatureToggleNames.EnforceMandatoryNotesForInsufficientInfoTasks is toggled on
 */
const triageReferralOld = async (customerId: string, caseId: string, createTasks: AddTaskModel[]): Promise<boolean> => {
    const response = await axios.post(`${apiUrl}/${customerId}/${caseId}/referral`,
        createTasks.map(task => ({
            taskType: task.type,
            taskCodeId: task.taskCodeId,
            dueDate: task.dueDate,
            notes: task.notes,
            assignedUsers: task.assignedUsers
        })));
    const result = Utils.CheckResponse(response.data);
    return result as boolean;
};

const triageReferral = async (customerId: string, caseId: string, createTasks: CreateTaskModel[]): Promise<boolean> => {
    const response = await axios.post<
        ResponseWrapper<boolean>,
        AxiosResponse<ResponseWrapper<boolean>>,
        ServerModelWithDateStrings<CreateTaskModel[]>>
        (`${apiUrl}/${customerId}/${caseId}/referral`, createTasks.map(t => mapCreateTaskModelToServer(t)));

    const result = Utils.CheckResponse<boolean>(response.data);
    return result as boolean;
};

const triagePhysiotherapyReferral = async (customerId: string, caseId: string, createTasks: AddTaskModel[]): Promise<boolean> => {
    const response = await axios.post(`${apiUrl}/${customerId}/${caseId}/physiotherapyreferral`,
        createTasks.map(task => ({
            taskType: task.type,
            taskCodeId: task.taskCodeId,
            dueDate: task.dueDate,
            notes: task.notes,
            assignedUsers: task.assignedUsers
        })));
    const result = Utils.CheckResponse(response.data);
    return result as boolean;
};

const triageGenericCase = async (customerId: string, caseId: string, createTasks: CreateTaskModel[]): Promise<boolean> => {
    const response = await axios.post<
        ResponseWrapper<boolean>,
        AxiosResponse<ResponseWrapper<boolean>>,
        ServerModelWithDateStrings<CreateTaskModel[]>>
        (`${apiUrl}/${customerId}/${caseId}/genericCase`, createTasks.map(t => mapCreateTaskModelToServer(t)));

    const result = Utils.CheckResponse<boolean>(response.data);
    return result as boolean;
};

const checkTriagePpqPossibility = async (
    triageId: string,
    caseId: string,
    customerId: string,
    employeeRecordId: string,
    createHealthSurveillanceCaseModel: HealthSurveillanceCaseCreateModel,
    createImmsCaseModel: ImmunisationCaseCreateModel,
    immsClearModel: models.ImmunisationsClearModel
): Promise<boolean> => {
    const response = await axios.post(
        `${apiUrl}/${triageId}/${caseId}/${customerId}/${employeeRecordId}/check`,
        { createHealthSurveillanceCaseModel, createImmsCaseModel, immsClearModel }
    );
    const result = Utils.CheckResponse(response.data);
    return result as boolean;
};

const ppqNoFurtherAction = async (customerId: string, caseId: string, comments: string, outcome: string): Promise<boolean> => {
    const response = await axios.post(`${apiUrl}/${customerId}/${caseId}/ppq/noFurtherAction`, { comments, outcome });
    const result = Utils.CheckResponse(response.data);
    return result as boolean;
};

const ppqTemporarilyUnfit = async (customerId: string, caseId: string, tasks: AddTaskModel[]): Promise<boolean> => {
    const response = await axios.post(`${apiUrl}/${customerId}/${caseId}/ppq/temporarilyUnfit`,
        tasks.map(task => ({
            taskType: task.type,
            taskCodeId: task.taskCodeId,
            dueDate: task.dueDate,
            notes: task.notes,
            assignedUsers: task.assignedUsers
        })));
    const result = Utils.CheckResponse(response.data);
    return result as boolean;
};

const ppqTemporarilyUnfitPendingFitForWork = async (customerId: string, caseId: string, comments: string): Promise<boolean> => {
    const response = await axios.post(`${apiUrl}/${customerId}/${caseId}/ppq/temporarilyUnfitPendingFitForWork`,
        { comments, outcome: models.PpqOutcomeId.temporarilyUnfitPendingFitForWork });
    const result = Utils.CheckResponse(response.data);
    return result as boolean;
};

const parseReferralModel = (model: ServerModelWithDateStrings<models.ReferralTriageDetailModel>): models.ReferralTriageDetailModel => {
    return {
        ...model,
        dateOfBirth: model.dateOfBirth ? moment(model.dateOfBirth, "YYYY-MM-DD") : null,
        lastClinicalActionDate: model.lastClinicalActionDate ? moment(model.lastClinicalActionDate) : null,
        lastReportDate: model.lastReportDate ? moment(model.lastReportDate) : null
    };
};

const parsePpqModel = (model: ServerModelWithDateStrings<models.PpqTriageDetailModel>): models.PpqTriageDetailModel => {
    return {
        ...model,
        dateOfBirth: model.dateOfBirth ? moment(model.dateOfBirth, "YYYY-MM-DD") : null
    };
};

const parsePhysiotherapyReferralModel = (
    model: ServerModelWithDateStrings<models.PhysiotherapyReferralTriageDetailModel>
): models.PhysiotherapyReferralTriageDetailModel => ({
    ...model,
    referredDob: model.referredDob ? moment(model.referredDob, "YYYY-MM-DD") : null,
    lastReportDate: model.lastReportDate ? moment(model.lastReportDate) : null,
});

const parseGenericCaseModel = (model: ServerModelWithDateStrings<models.GenericCaseTriageDetailModel>): models.GenericCaseTriageDetailModel => {
    return {
        ...model,
        dateOfBirth: model.dateOfBirth ? moment(model.dateOfBirth, "YYYY-MM-DD") : null
    };
};

const parseListModel = (model: ServerModelWithDateStrings<models.TriageListModel>): models.TriageListModel => {
    return {
        ...model,
        referralCreated: model.referralCreated ? moment(model.referralCreated) : null,
        dateCreated: model.dateCreated ? moment(model.dateCreated) : null
    };
};

export const TriageApi = {
    get,
    getPhysiotherapyReferral,
    getReferral,
    getPpq,
    getGenericCase,
    checkTriagePpqPossibility,
    getReferralTriageTasks,
    getPpqTriageTasks,
    getPhysiotherapyReferralTriageSuccessorTasks,
    getGenericCaseTriageSuccessorTasks,
    triageReferralOld,
    triageReferral,
    triagePhysiotherapyReferral,
    triageGenericCase,
    ppqNoFurtherAction,
    ppqTemporarilyUnfit,
    ppqTemporarilyUnfitPendingFitForWork,
};
